/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import DS from 'ember-data';
import AccountService from 'cio/services/account';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { FlyMouseEvent } from '@customerio/fly-components/types/events';
import rangeSelectionHelper from 'cio/utils/range-selection-helper';
import ObjectTypeModel, { ObjectTypeUsage } from 'cio/models/object-type';
import { dropTask } from 'ember-concurrency';
import AlertsService from 'cio/services/alerts';
import ObjectTypesService from 'cio/services/object-types';

interface Args {
  objectTypesUsage: Record<string, ObjectTypeUsage>;
}

export default class ObjectsTableComponent extends Component<Args> {
  @service store!: DS.Store;
  @service accounts!: AccountService;
  @service alerts!: AlertsService;
  @service('objectTypes') objectTypesService!: ObjectTypesService;

  @tracked selectedObjectTypes: ObjectTypeModel[] = [];
  @tracked allSelected = false;
  @tracked showDeleteModal = false;

  @tracked private shiftSelectStart: number | null = null;
  @tracked private shiftSelectEnd: number | null = null;

  get objectTypes() {
    return this.store
      .peekAll('object-type')
      .toArray()
      .sort((a: ObjectTypeModel, b: ObjectTypeModel) => {
        if (a.name < b.name) {
          return -1;
        }

        if (a.name > b.name) {
          return 1;
        }

        return 0;
      });
  }

  get totalObjectTypes() {
    return this.objectTypes.toArray().length;
  }

  get allObjectsOnPageIndeterminate() {
    if (this.allSelected) {
      return false;
    }

    return this.selectedObjectTypes.length > 0 && this.selectedObjectTypes.length < this.totalObjectTypes;
  }

  get isDeleteButtonDisabled() {
    return (
      this.selectedObjectTypes.some((objectType) =>
        Object.values(this.args.objectTypesUsage[objectType.id]).some((count) => count > 0),
      ) || !this.objectTypesService.canConfigureObjectTypes
    );
  }

  @action
  objectTypeChecked(
    obj: ObjectTypeModel,
    index: number,
    checked: boolean,
    metadata: { event: FlyMouseEvent<HTMLInputElement> },
  ) {
    let { tempShiftSelectEnd, tempShiftSelectStart, allSelected } = rangeSelectionHelper<ObjectTypeModel>({
      models: this.objectTypes as any,
      singleModel: obj,
      selectedItems: this.selectedObjectTypes,
      index,
      checked,
      totalRecs: this.totalObjectTypes,
      metadata,
      shiftSelectStart: this.shiftSelectStart,
      shiftSelectEnd: this.shiftSelectEnd,
    });

    this.shiftSelectStart = tempShiftSelectStart;
    this.shiftSelectEnd = tempShiftSelectEnd;
    this.allSelected = allSelected;
  }

  @action
  selectAllObjectsOnPage(checked: boolean) {
    if (checked) {
      this.selectedObjectTypes = this.objectTypes.slice();
      this.allSelected = true;
    } else {
      this.clearSelection();
    }
  }

  @action
  clearSelection() {
    this.selectedObjectTypes = [];
    this.allSelected = false;
  }

  @action
  onModalClose() {
    this.showDeleteModal = false;
    this.clearSelection();
  }

  toggleObjectTypeTask = dropTask(async (objectType: ObjectTypeModel, active: boolean) => {
    let initialState = objectType.enabled;
    objectType.enabled = active;

    try {
      await objectType.save();
      this.alerts.success(
        'object_type_status_update.success',
        `${objectType.name} has been ${active ? 'enabled' : 'disabled'}`,
      );
    } catch {
      objectType.enabled = initialState;
      this.alerts.error(
        'object_type_status_update.error',
        `Could not ${active ? 'enable' : 'disable'} ${objectType.name}`,
      );
    }
  });
}
